import React, { useState, useEffect, ChangeEvent } from "react"
import { graphql } from "gatsby"

import PublicationCard from "../components/PublicationCard"
import PublicationSelector from "../components/PublicationSelector"
import SearchInput from "../components/SearchInput"

const pubTypes = [
  "all publications",
  "journal article",
  "technical report",
  "proceedings paper",
  "research product",
  "other report",
]

const publicationGroups = [
  ["technical report", "Technical reports"],
  ["journal article", "Journal articles"],
  ["proceedings paper", "Proceedings papers"],
  ["research product", "Research products"],
  ["other report", "Other reports"],
]

const PublicationsPage = ({ navigate, data }) => {
  const allPublications = data.allStothartPublicationsJson.nodes
  const [pubSearchInput, setPubSearchInput] = useState("")
  const [selectedPubData, setSelectedPubData] = useState(allPublications)
  const [selectedPubCount, setSelectedPubCount] = useState(
    allPublications.length
  )

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPubSearchInput(event.target.value)
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      if (window.location.search) {
        try {
          let query = window.location.search.split("=")[1]
          query = decodeURI(query)
          setPubSearchInput(query)
        } catch {}
      }
    }
  }, [])

  useEffect(() => {
    const pubSearchInputArray = pubSearchInput.toLowerCase().trim().split(" ")

    const newSelectedPubData = data.allStothartPublicationsJson.nodes.filter(
      pub => {
        const authors = data.allStothartCollaboratorsJson.nodes.filter(
          author => {
            return pub.collaborators.includes(author.id)
          }
        )

        let inputWordsFound = 0

        for (const inputWord of pubSearchInputArray) {
          let inputWordFound = false
          for (const property in pub) {
            if (pub[property] && property !== "publisherUrl") {
              const propertyValue = pub[property].toString().toLowerCase()
              if (propertyValue.includes(inputWord)) {
                inputWordFound = true
              }
            }

            if (property === "mediaCoverage" && pub[property]) {
              if ("featured in".includes(inputWord)) {
                inputWordFound = true
              }
              for (const article of pub[property]) {
                const publisher = article.publisher.toString().toLowerCase()
                if (publisher.includes(inputWord)) {
                  inputWordFound = true
                }
              }
            }

            if (property === "year" && pub[property] === 999) {
              if ("in press".includes(inputWord)) {
                inputWordFound = true
              }
            }
          }

          for (const author of authors) {
            for (const authorProperty in author) {
              if (author[authorProperty]) {
                if (
                  author[authorProperty]
                    .toString()
                    .toLowerCase()
                    .includes(inputWord)
                ) {
                  inputWordFound = true
                }
              }
            }
          }

          if (inputWordFound) inputWordsFound += 1
        }

        let pubTypeFound = null
        for (const pubType of pubTypes) {
          if (pubSearchInput.toLowerCase().trim().includes(pubType)) {
            pubTypeFound = pubType
          }
        }

        if (pubTypeFound) {
          if (pubTypeFound === pub.publicationType) {
            return inputWordsFound === pubSearchInputArray.length ? true : false
          }
        } else {
          return inputWordsFound === pubSearchInputArray.length ? true : false
        }
      }
    )

    setSelectedPubData(newSelectedPubData)
    setSelectedPubCount(newSelectedPubData.length)

    if (pubSearchInput.trim().length > 0) {
      navigate(`?q=${pubSearchInput}`)
    } else {
      navigate("/publications")
    }
  }, [pubSearchInput])

  const publicationSelectors = pubTypes.map(pubType => {
    let isOn = false
    if (pubType === "all publications") {
      isOn = pubSearchInput.trim() === "" ? true : false
    } else {
      isOn = pubSearchInput.toLowerCase().includes(pubType) ? true : false
    }

    return (
      <PublicationSelector
        key={pubType}
        selectorString={pubType}
        count={
          pubType === "all publications"
            ? allPublications.length
            : allPublications.filter(pub => pub.publicationType === pubType)
                .length
        }
        onClick={() => {
          if (pubType === "all publications") {
            setPubSearchInput("")
            navigate("")
          } else {
            setPubSearchInput(pubType)
            navigate(`?q=${pubType}`)
          }
        }}
        on={isOn}
      />
    )
  })

  const renderPublication = pub => {
    const authors = pub.collaborators.map(pubAuthorId => {
      let authorObject = {}
      for (const author of data.allStothartCollaboratorsJson.nodes) {
        if (pubAuthorId === author.id) {
          authorObject = author
        }
      }
      return authorObject
    })

    return (
      <PublicationCard
        key={pub.id}
        title={pub.title}
        subtitle={pub.subtitle ? pub.subtitle : null}
        publisher={pub.publisher}
        publisherUrl={pub.publisherUrl}
        downloadUrl={pub.downloadUrl}
        publicationType={pub.publicationType}
        year={pub.year}
        authors={authors}
        doi={pub.doi}
        mediaCoverage={pub.mediaCoverage}
        podcast={pub.podcast}
        summaryPath={pub.summaryPath}
      />
    )
  }

  return (
    <>
      <div className="page-intro">
        <div className="page-intro__title-row">
          <h1>Publications</h1>
          <span aria-label={`${selectedPubCount} publications`}>
            {selectedPubCount}
          </span>
        </div>
      </div>
      <div className="listing-search">
        <SearchInput
          label="Search Publications"
          handleInputChange={handleInputChange}
          searchInput={pubSearchInput}
          searchResultCount={selectedPubCount}
        />
      </div>

      <div
        className="filter-list filter-list--publications"
        role="group"
        aria-label="Filter publications by type"
      >
        {publicationSelectors}
      </div>

      <div
        key={pubSearchInput.trim().toLowerCase() || "all-publications"}
        className="filter-results publication-groups"
      >
        {selectedPubCount > 0 ? (
          publicationGroups.map(([type, label]) => {
            const entries = selectedPubData
              .filter(pub => pub.publicationType === type)
              .sort((a, b) => {
                const yearA = a.year === 999 ? 9999 : a.year
                const yearB = b.year === 999 ? 9999 : b.year
                return yearB - yearA
              })
            if (!entries.length) return null
            const headingId = `publication-group-${type.replace(/\s+/g, "-")}`
            return (
              <section
                className="publication-group"
                aria-labelledby={headingId}
                key={type}
              >
                <div className="section-title-row">
                  <h2 id={headingId}>{label}</h2>
                  <span>{entries.length}</span>
                </div>
                <div>{entries.map(renderPublication)}</div>
              </section>
            )
          })
        ) : (
          <p className="empty-results" role="status">
            No publications found.
          </p>
        )}
      </div>
    </>
  )
}

export const query = graphql`
  query {
    allStothartPublicationsJson {
      nodes {
        id
        publicationType
        publisher
        publisherUrl
        downloadUrl
        subtitle
        title
        year
        collaborators
        doi
        podcast
        summaryPath
        mediaCoverage {
          publisher
          url
        }
      }
    }
    allStothartCollaboratorsJson {
      nodes {
        id
        familyName
        firstName
        middleInitial
      }
    }
  }
`

export default PublicationsPage
